/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Découpage Pro - Shots Table Component
 * Professional cinema shotlist table with timecode, camera controls, and engine selector
 */
import React, { useState, useCallback } from 'react';
import {

    ShotSpec,
    ShotScale,
    ShotAngle,
    ShotMovement,
    ImageEngine,
    ShotStatus,
    SHOT_SCALES,
    SHOT_ANGLES,
    SHOT_MOVEMENTS,
    SCALE_DISPLAY_NAMES,
    ANGLE_DISPLAY_NAMES,
    MOVEMENT_DISPLAY_NAMES,
    tcDurationSec,
    createEmptyShot,
    getNextTcIn,
} from '../types/decoupage';
import { getImageModels, getModelById } from '../services/modelRegistry';
import {
    PlusIcon,
    TrashIcon,
    PencilIcon,
    CheckIcon,
    XMarkIcon,
    SparklesIcon,
    CopyIcon,
    LockIcon,
} from './icons';

import { ImageFile } from '../types';

interface ShotsTableProps {
    shots: ShotSpec[];
    fps: number;
    onUpdateShot: (id: string, updates: Partial<ShotSpec>) => void;
    onDeleteShot: (id: string) => void;
    onAddShot: () => void;
    onDuplicateShot: (id: string) => void;
    onGenerateCandidates: (shot: ShotSpec) => void;
    onSelectShot: (shot: ShotSpec) => void;
    selectedShotId?: string;
    isGenerating?: boolean;
    isReadOnly?: boolean;
    // Master Shot UX
    masterShot?: ImageFile | null;
    onRequestMasterShot?: () => void;
    onClearMasterShot?: () => void;
}

const ShotsTable: React.FC<ShotsTableProps> = ({
    shots,
    fps,
    onUpdateShot,
    onDeleteShot,
    onAddShot,
    onDuplicateShot,
    onGenerateCandidates,
    onSelectShot,
    selectedShotId,
    isGenerating = false,
    isReadOnly = false,
    // Master Shot UX
    masterShot,
    onRequestMasterShot,
    onClearMasterShot,
}) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<Partial<ShotSpec>>({});

    const startEdit = useCallback((shot: ShotSpec) => {
        setEditingId(shot.id);
        setEditValues({
            tc_in: shot.tc_in,
            tc_out: shot.tc_out,
            camera: { ...shot.camera },
            prompt_notes: shot.prompt_notes,
            action: shot.action,
            image_provider: { ...shot.image_provider },
        });
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingId(null);
        setEditValues({});
    }, []);

    const saveEdit = useCallback(() => {
        if (editingId) {
            onUpdateShot(editingId, editValues);
            setEditingId(null);
            setEditValues({});
        }
    }, [editingId, editValues, onUpdateShot]);

    const updateEditValue = useCallback((key: string, value: any) => {
        setEditValues(prev => {
            if (key.startsWith('camera.')) {
                const cameraKey = key.replace('camera.', '');
                return {
                    ...prev,
                    camera: { ...prev.camera, [cameraKey]: value },
                };
            }

            return { ...prev, [key]: value };
        });
    }, []);

    const getStatusBadge = (status: ShotStatus) => {
        const badges: Record<ShotStatus, { bg: string; text: string; label: string }> = {
            pending: { bg: 'bg-gray-600', text: 'text-gray-300', label: 'En attente' },
            generating: { bg: 'bg-amber-600', text: 'text-amber-100', label: 'Génération...' },
            candidates_ready: { bg: 'bg-blue-600', text: 'text-blue-100', label: 'Candidats' },
            locked: { bg: 'bg-green-600', text: 'text-green-100', label: 'Verrouillé' },
            video_generating: { bg: 'bg-purple-600', text: 'text-purple-100', label: 'Vidéo...' },
            video_ready: { bg: 'bg-emerald-600', text: 'text-emerald-100', label: 'Prêt' },
        };
        const badge = badges[status];
        return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    return (
        <div className="flex flex-col h-full bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <span className="material-symbols-outlined text-indigo-400">movie_filter</span>
                    Découpage Pro
                </h2>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">{shots.length} plans • {fps} fps</span>
                    {!isReadOnly && (
                        <button
                            onClick={onAddShot}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Ajouter
                        </button>
                    )}
                </div>
            </div>

            {/* Step Indicator + Visual Context Panel */}
            <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                {/* Step Indicator */}
                <div className="flex items-center gap-3 mb-3">
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${shots.length > 0 ? 'bg-green-600/20 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                        <span className="material-symbols-outlined text-sm">edit_note</span>
                        1. Prompts {shots.length > 0 && '✓'}
                    </div>
                    <span className="text-gray-600">→</span>
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${masterShot ? 'bg-green-600/20 text-green-400' : 'bg-indigo-600/20 text-indigo-400'}`}>
                        <span className="material-symbols-outlined text-sm">palette</span>
                        2. Style {masterShot ? '✓' : '(optionnel)'}
                    </div>
                    <span className="text-gray-600">→</span>
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${shots.some(s => s.status === 'locked' || s.status === 'video_ready') ? 'bg-green-600/20 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                        <span className="material-symbols-outlined text-sm">image</span>
                        3. Vignettes
                    </div>
                </div>

                {/* Visual Context Panel */}
                <div className={`flex items-center gap-4 p-3 rounded-lg border ${masterShot ? 'bg-emerald-900/10 border-emerald-600/30' : 'bg-indigo-900/10 border-indigo-600/30'}`}>
                    {/* Master Shot Preview or Placeholder */}
                    <div className="flex-shrink-0">
                        {masterShot ? (
                            <div className="relative w-20 h-14 rounded-lg overflow-hidden border-2 border-emerald-500">
                                <img
                                    src={`data:image/png;base64,${masterShot.base64}`}
                                    alt="Style de référence"
                                    className="w-full h-full object-cover"
                                />
                                {onClearMasterShot && (
                                    <button
                                        onClick={onClearMasterShot}
                                        className="absolute top-0 right-0 p-0.5 bg-red-600 text-white rounded-bl-lg text-xs"
                                        title="Supprimer"
                                    >
                                        <XMarkIcon className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="w-20 h-14 rounded-lg border-2 border-dashed border-indigo-500/50 flex items-center justify-center bg-gray-800/50">
                                <span className="material-symbols-outlined text-indigo-400 text-2xl">palette</span>
                            </div>
                        )}
                    </div>

                    {/* Description & Actions */}
                    <div className="flex-1 min-w-0">
                        {masterShot ? (
                            <>
                                <p className="text-sm font-medium text-emerald-300">Style de référence défini ✓</p>
                                <p className="text-xs text-gray-400 truncate">Les vignettes seront cohérentes avec ce style visuel</p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium text-indigo-300">Style de référence (optionnel)</p>
                                <p className="text-xs text-gray-400">Générez une image de style pour garantir la cohérence visuelle</p>
                            </>
                        )}
                    </div>

                    {/* Action Button */}
                    {!masterShot && onRequestMasterShot && (
                        <button
                            onClick={onRequestMasterShot}
                            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            <SparklesIcon className="w-4 h-4" />
                            Définir le style
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-800/50 sticky top-0">
                        <tr className="border-b border-gray-700">
                            <th className="px-3 py-2 text-left text-gray-400 font-medium w-12">#</th>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">TC In</th>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">TC Out</th>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">Durée</th>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">Valeur</th>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">Axe</th>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">Optique</th>
                            <th className="px-3 py-2 text-left text-gray-400 font-medium">Mouvement</th>

                            <th className="px-3 py-2 text-left text-gray-400 font-medium">Status</th>
                            <th className="px-3 py-2 text-right text-gray-400 font-medium">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shots.map((shot, index) => {
                            const isEditing = editingId === shot.id;
                            const isSelected = selectedShotId === shot.id;
                            const duration = tcDurationSec(shot.tc_in, shot.tc_out, fps);

                            return (
                                <tr
                                    key={shot.id}
                                    className={`border-b border-gray-800 hover:bg-gray-800/30 cursor-pointer transition-colors ${isSelected ? 'bg-indigo-900/20 border-indigo-500' : ''
                                        }`}
                                    onClick={() => !isEditing && onSelectShot(shot)}
                                >
                                    {/* Number */}
                                    <td className="px-3 py-2 text-gray-400 font-mono">
                                        {shot.number || index + 1}
                                    </td>

                                    {/* TC In */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={editValues.tc_in || ''}
                                                onChange={(e) => updateEditValue('tc_in', e.target.value)}
                                                className="w-24 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white font-mono text-xs"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span className="font-mono text-gray-200">{shot.tc_in}</span>
                                        )}
                                    </td>

                                    {/* TC Out */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={editValues.tc_out || ''}
                                                onChange={(e) => updateEditValue('tc_out', e.target.value)}
                                                className="w-24 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white font-mono text-xs"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span className="font-mono text-gray-200">{shot.tc_out}</span>
                                        )}
                                    </td>

                                    {/* Duration */}
                                    <td className="px-3 py-2 text-gray-300 font-mono">
                                        {duration.toFixed(2)}s
                                    </td>

                                    {/* Scale */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <select
                                                value={editValues.camera?.scale || 'plan_moyen'}
                                                onChange={(e) => updateEditValue('camera.scale', e.target.value)}
                                                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {SHOT_SCALES.map(scale => (
                                                    <option key={scale} value={scale}>
                                                        {SCALE_DISPLAY_NAMES[scale]}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className="text-gray-200">
                                                {SCALE_DISPLAY_NAMES[shot.camera.scale]}
                                            </span>
                                        )}
                                    </td>

                                    {/* Axis */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                value={editValues.camera?.axis_deg ?? 0}
                                                onChange={(e) => updateEditValue('camera.axis_deg', parseInt(e.target.value) || 0)}
                                                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs"
                                                min={-180}
                                                max={180}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span className="text-gray-200">{shot.camera.axis_deg}°</span>
                                        )}
                                    </td>

                                    {/* Lens */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                value={editValues.camera?.lens_mm ?? 35}
                                                onChange={(e) => updateEditValue('camera.lens_mm', parseInt(e.target.value) || 35)}
                                                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs"
                                                min={8}
                                                max={300}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span className="text-gray-200">{shot.camera.lens_mm}mm</span>
                                        )}
                                    </td>

                                    {/* Movement */}
                                    <td className="px-3 py-2">
                                        {isEditing ? (
                                            <select
                                                value={editValues.camera?.movement || 'fixe'}
                                                onChange={(e) => updateEditValue('camera.movement', e.target.value)}
                                                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {SHOT_MOVEMENTS.map(mov => (
                                                    <option key={mov} value={mov}>
                                                        {MOVEMENT_DISPLAY_NAMES[mov]}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className="text-gray-200">
                                                {MOVEMENT_DISPLAY_NAMES[shot.camera.movement]}
                                            </span>
                                        )}
                                    </td>



                                    {/* Status */}
                                    <td className="px-3 py-2">
                                        {shot.status === 'locked' && <LockIcon className="w-3 h-3 text-green-400 inline mr-1" />}
                                        {getStatusBadge(shot.status)}
                                    </td>

                                    {/* Actions */}
                                    <td className="px-3 py-2">
                                        <div className="flex items-center justify-end gap-1">
                                            {isEditing ? (
                                                <>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                                                        className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                                                        title="Enregistrer"
                                                    >
                                                        <CheckIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                                                        className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                                                        title="Annuler"
                                                    >
                                                        <XMarkIcon className="w-4 h-4" />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onGenerateCandidates(shot); }}
                                                        disabled={isGenerating || shot.status === 'generating'}
                                                        className="p-1.5 text-indigo-400 hover:bg-indigo-500/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Générer vignettes"
                                                    >
                                                        <SparklesIcon className="w-4 h-4" />
                                                    </button>
                                                    {!isReadOnly && (
                                                        <>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); startEdit(shot); }}
                                                                className="p-1.5 text-gray-400 hover:bg-gray-700 rounded"
                                                                title="Modifier"
                                                            >
                                                                <PencilIcon className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); onDuplicateShot(shot.id); }}
                                                                className="p-1.5 text-gray-400 hover:bg-gray-700 rounded"
                                                                title="Dupliquer"
                                                            >
                                                                <CopyIcon className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); onDeleteShot(shot.id); }}
                                                                className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                                                                title="Supprimer"
                                                            >
                                                                <TrashIcon className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {shots.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <span className="material-symbols-outlined text-4xl mb-2">movie_filter</span>
                        <p>Aucun plan défini</p>
                        <button
                            onClick={onAddShot}
                            className="mt-4 flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Ajouter un plan
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShotsTable;
